import React, {
  createContext,
  useCallback,
  useContext,
  useReducer
} from 'react';
import { ReactEditor } from 'slate-react';

export type Tab = 'partials' | 'finals';

interface EditorState {
  editors: Record<string, ReactEditor | null>;
  activeTab: Tab;
}

type EditorAction =
  | { type: 'ADD_EDITOR'; payload: { id: string; editor: ReactEditor } }
  | { type: 'REMOVE_EDITOR'; payload: { id: string } }
  | { type: 'SET_ACTIVE_TAB'; payload: Tab }
  | { type: 'RESET' };

const initialState: EditorState = {
  editors: {},
  activeTab: 'partials'
};

function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'ADD_EDITOR':
      return {
        ...state,
        editors: {
          ...state.editors,
          [action.payload.id]: action.payload.editor
        }
      };
    case 'REMOVE_EDITOR': {
      const updatedEditors = { ...state.editors };
      delete updatedEditors[action.payload.id];
      return { ...state, editors: updatedEditors };
    }
    case 'SET_ACTIVE_TAB':
      return {
        ...state,
        activeTab: action.payload
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

const RealtimeEditorContext = createContext<{
  editors: Record<string, ReactEditor | null>;
  activeTab: Tab;
  addEditor: (id: string, editor: ReactEditor) => void;
  removeEditor: (id: string) => void;
  trackModification: (editor: ReactEditor) => void;
  setActiveTab: (tab: Tab) => void;
  reset: () => void;
}>({
  editors: {},
  activeTab: 'partials',
  addEditor: () => {},
  removeEditor: () => {},
  trackModification: () => {},
  setActiveTab: () => {},
  reset: () => {}
});

export const RealtimeEditorProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(editorReducer, initialState);

  const addEditor = useCallback((id: string, editor: ReactEditor) => {
    dispatch({
      type: 'ADD_EDITOR',
      payload: { id: `realtime-editor-${id}`, editor }
    });
  }, []);

  const removeEditor = useCallback((id: string) => {
    dispatch({
      type: 'REMOVE_EDITOR',
      payload: { id: `realtime-editor-${id}` }
    });
  }, []);

  const trackModification = useCallback((_editor: ReactEditor) => {
    // No-op for now, can be extended later for undo/redo
  }, []);

  const setActiveTab = useCallback((tab: Tab) => {
    dispatch({
      type: 'SET_ACTIVE_TAB',
      payload: tab
    });
  }, []);
  const reset = useCallback(() => dispatch({ type: 'RESET' }), []);

  return (
    <RealtimeEditorContext.Provider
      value={{
        editors: state.editors,
        activeTab: state.activeTab,
        addEditor,
        removeEditor,
        trackModification,
        setActiveTab,
        reset
      }}
    >
      {children}
    </RealtimeEditorContext.Provider>
  );
};

export const useRealtimeEditorContext = () => useContext(RealtimeEditorContext);
